import { useState, useMemo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Search } from "lucide-react";
import { useWineLots, WineLotWithClient } from "@/hooks/useWineLots";
import { useClients, Client } from "@/hooks/useClients";
import { CreateLotDialog } from "@/components/lots/CreateLotDialog";
import { LotDetailSheet } from "@/components/lots/LotDetailSheet";
import { LotsTable, SortField, SortDirection } from "./LotsTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Debounce hook for search input
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function LotsTab() {
  const [searchTerm, setSearchTerm] = useState("");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("created");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedLot, setSelectedLot] = useState<WineLotWithClient | null>(null);
  const [lotSheetOpen, setLotSheetOpen] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 300);

  // Fetch all lots (search by code/varietal happens in the hook)
  const { data: allLots = [], isLoading: lotsLoading } = useWineLots(debouncedSearch);

  // Fetch clients for filter dropdown
  const { data: clients = [] } = useClients();

  // Apply client filter and client name search client-side
  const filteredLots = useMemo(() => {
    let result = allLots;

    // Apply client filter
    if (clientFilter !== "all") {
      result = result.filter(lot => lot.client_id === clientFilter);
    }

    // If search term exists, also filter by client name (code/varietal handled by hook)
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(lot =>
        lot.code.toLowerCase().includes(searchLower) ||
        lot.varietal?.toLowerCase().includes(searchLower) ||
        lot.client?.name?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [allLots, clientFilter, debouncedSearch]);

  // Sort lots client-side
  const sortedLots = useMemo(() => {
    const sorted = [...filteredLots];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "code":
          comparison = a.code.localeCompare(b.code);
          break;
        case "client":
          comparison = (a.client?.name || "").localeCompare(b.client?.name || "");
          break;
        case "varietal":
          comparison = (a.varietal || "").localeCompare(b.varietal || "");
          break;
        case "vintage":
          comparison = (a.vintage || 0) - (b.vintage || 0);
          break;
        case "volume":
          comparison = (a.current_volume_l || 0) - (b.current_volume_l || 0);
          break;
        case "created":
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [filteredLots, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleRowClick = (lot: WineLotWithClient) => {
    setSelectedLot(lot);
    setLotSheetOpen(true);
  };

  const showEmptyState = !lotsLoading && sortedLots.length === 0;
  const hasFilters = searchTerm || clientFilter !== "all";

  return (
    <>
      <div className="space-y-4">
        {/* Search and Filter Bar */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by code, varietal, or client..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((client: Client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results count */}
        {!lotsLoading && (
          <p className="text-sm text-muted-foreground">
            {sortedLots.length} {sortedLots.length === 1 ? "lot" : "lots"}
            {hasFilters && " found"}
          </p>
        )}

        {/* Table or Empty State */}
        {showEmptyState ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground mb-4">
              {hasFilters ? "No lots match your search" : "No wine lots yet"}
            </p>
            {!hasFilters && <CreateLotDialog />}
          </Card>
        ) : (
          <LotsTable
            lots={sortedLots}
            isLoading={lotsLoading}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onRowClick={handleRowClick}
          />
        )}
      </div>

      <LotDetailSheet
        lot={selectedLot}
        open={lotSheetOpen}
        onOpenChange={setLotSheetOpen}
      />
    </>
  );
}
